import {
  useCreateUniversityMutation,
  useLazyGetUniversityQuery,
  useUpdateUniversityMutation,
} from "@api/university";
import Header from "@components/Header";
import { AcademicCapIcon } from "@heroicons/react/24/outline";
import { useToastEffect } from "@hooks/hooks";
import { CreateUniversityDTO, PatchUniversityDTO } from "@onetutor/shared/dto";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CognitoGroupsManager from "./components/CognitoGroupsManager";
import UniversityDetailsForm from "./components/UniversityDetailsForm";
import UniversityList from "./components/UniversityList";

const UniversityManagementPage = () => {
  const { t } = useTranslation(["universityManagement", "toast", "commons"]);
  const [selectedUniversityId, setSelectedUniversityId] = useState<string>();
  const [isCreating, setIsCreating] = useState(false);

  const [
    triggerGetUniversity,
    { data: universityData, reset: resetGetUniversity },
  ] = useLazyGetUniversityQuery();

  const [
    createUniversity,
    {
      isSuccess: isSuccessCreateUniversity,
      isError: isErrorCreateUniversity,
      reset: resetCreateUniversity,
    },
  ] = useCreateUniversityMutation();

  const [
    updateUniversity,
    {
      isSuccess: isSuccessUpdateUniversity,
      isError: isErrorUpdateUniversity,
      reset: resetUpdateUniversity,
    },
  ] = useUpdateUniversityMutation();

  const [university, setUniversity] = useState<PatchUniversityDTO>({});

  useToastEffect({
    isSuccess: isSuccessCreateUniversity,
    successMessage: t("universityManagementPage.successCreateUniversity", {
      ns: "toast",
    }),
    isError: isErrorCreateUniversity,
  });

  useToastEffect({
    isSuccess: isSuccessUpdateUniversity,
    successMessage: t("universityManagementPage.successUpdateUniversity", {
      ns: "toast",
    }),
    isError: isErrorUpdateUniversity,
  });

  useEffect(() => {
    if (!universityData) {
      return;
    }
    setUniversity({
      fullName: universityData.fullName,
      shortName: universityData.shortName,
      color: universityData.color,
      maxCourseCount: universityData.maxCourseCount,
      instanceUrl: universityData.instanceUrl || null,
      idpId: universityData.idpId || null,
    });
  }, [universityData]);

  useEffect(() => {
    if (!isSuccessCreateUniversity) {
      return;
    }
    resetCreateUniversity();
    setIsCreating(false);
    setUniversity({});
  }, [isSuccessCreateUniversity, resetCreateUniversity]);

  useEffect(() => {
    if (!isSuccessUpdateUniversity) {
      return;
    }
    resetUpdateUniversity();
    if (selectedUniversityId) {
      triggerGetUniversity(selectedUniversityId);
    }
  }, [
    isSuccessUpdateUniversity,
    resetUpdateUniversity,
    triggerGetUniversity,
    selectedUniversityId,
  ]);

  const handleSelectUniversity = (universityId: string) => {
    setSelectedUniversityId(universityId);
    setIsCreating(false);
    triggerGetUniversity(universityId);
  };

  const handleCreateNewUniversity = () => {
    setIsCreating(true);
    setSelectedUniversityId(undefined);
    setUniversity({
      fullName: "",
      shortName: "",
      color: "#0066cc",
      maxCourseCount: 20,
      instanceUrl: null,
      idpId: null,
    });
  };

  const buildUniversityDTO = (
    university: PatchUniversityDTO,
    isCreating: boolean,
  ): CreateUniversityDTO | PatchUniversityDTO => ({
    fullName: university.fullName?.trim() || (isCreating ? "" : undefined),
    shortName: university.shortName?.trim() || (isCreating ? "" : undefined),
    color: university.color || (isCreating ? "#0066cc" : undefined),
    maxCourseCount: university.maxCourseCount || (isCreating ? 20 : undefined),
    instanceUrl: university.instanceUrl?.trim() || null,
    idpId: university.idpId?.trim() || null,
  });

  const handleSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isCreating) {
      createUniversity(
        buildUniversityDTO(university, true) as CreateUniversityDTO,
      );
      return;
    }
    if (!selectedUniversityId) {
      return;
    }
    updateUniversity({
      universityId: selectedUniversityId,
      university: buildUniversityDTO(university, false) as PatchUniversityDTO,
    });
  };

  const handleUniversityChange = (
    field: keyof PatchUniversityDTO,
    value: string | number | null,
  ) => {
    setUniversity((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Header breadcrumbs={[{ name: t("universityManagement") }]}>
      <div className="mt-4 mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <AcademicCapIcon className="mr-2 h-8 w-8" />
          <h1 className="font-bold text-2xl">{t("universityManagement")}</h1>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleCreateNewUniversity}
        >
          {t("createUniversity")}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <UniversityList
            selectedUniversityId={selectedUniversityId}
            onSelectUniversity={handleSelectUniversity}
          />
        </div>

        <div className="lg:col-span-2">
          {universityData || isCreating ? (
            <div className="space-y-6">
              <UniversityDetailsForm
                university={university}
                universityId={isCreating ? undefined : selectedUniversityId}
                isCreating={isCreating}
                onSubmit={handleSubmitForm}
                onUniversityChange={handleUniversityChange}
                onDeleteSuccess={() => {
                  setSelectedUniversityId(undefined);
                  setIsCreating(false);
                  resetGetUniversity();
                }}
              />

              {!isCreating && selectedUniversityId && (
                <CognitoGroupsManager
                  selectedUniversityId={selectedUniversityId}
                />
              )}
            </div>
          ) : (
            <div className="card bg-base-200 shadow-lg">
              <div className="card-body">
                <div className="py-8 text-center text-gray-500">
                  {t("selectUniversity")}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Header>
  );
};

export default UniversityManagementPage;
